import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Pencil, Trash2, Plus, ChevronDown, ChevronRight } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { Button } from '@/components/ui/button'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { EntityWithSupports, FormationNameOptionData } from '@/types'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import { FormationNameOptionSheet } from '../../../components/backoffice/FormationNameOptionSheet'
import { formatCents } from '@/lib/utils'

type Props = { entity: EntityWithSupports }

type SupportSectionProps = {
  title: string
  supportType: string
  entityId: string
  items: Record<string, unknown>[]
  fieldLabels: { key: string; label: string }[]
}

function SupportSection({ title, supportType, entityId, items, fieldLabels }: SupportSectionProps) {
  const [expanded, setExpanded] = useState(items.length > 0)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  
  // Sheet state handling
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<FormationNameOptionData | null>(null)

  const isNameOption = supportType === 'formation_name_option'

  const basePath = isNameOption
    ? `/backoffice/entities/${entityId}/formation_name_options`
    : `/backoffice/entities/${entityId}/${supportType}_supports`

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isNameOption) {
      setEditingItem(null)
      setSheetOpen(true)
    }
  }

  const handleEditClick = (item: FormationNameOptionData) => {
    if (isNameOption) {
      setEditingItem(item)
      setSheetOpen(true)
    }
  }

  return (
    <div className="border-b border-border/40 last:border-0">
      <div
        className="flex items-center justify-between py-2 cursor-pointer hover:bg-muted/30 -mx-1 px-1 rounded-sm"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="text-sm font-medium">{title}</span>
          <span className="text-[12px] text-muted-foreground">{items.length || '—'}</span>
        </div>

        {isNameOption ? (
          <Button size="sm" variant="ghost" className="h-7 text-[12px] text-muted-foreground hover:text-foreground gap-1" onClick={handleAddClick}>
            <Plus className="w-3 h-3" /> Add
          </Button>
        ) : (
          <Button size="sm" variant="ghost" asChild onClick={(e) => e.stopPropagation()} className="h-7 text-[12px] text-muted-foreground hover:text-foreground gap-1">
            <Link href={`${basePath}/new`}>
              <Plus className="w-3 h-3" /> Add
            </Link>
          </Button>
        )}
      </div>

      {expanded && items.length > 0 && (
        <div className="pl-6 pb-3 space-y-1.5">
          {items.map((item) => (
            <div key={item.id as string} className="flex items-center justify-between py-1.5 group">
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[13px]">
                {fieldLabels.map(({ key, label }) => (
                  <span key={key} className="text-muted-foreground">
                    {label}: <span className="text-foreground">{String(item[key] ?? '—')}</span>
                  </span>
                ))}
              </div>
              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {isNameOption ? (
                  <Button size="icon" variant="ghost" onClick={() => handleEditClick(item)} className="h-6 w-6">
                    <Pencil className="h-3 w-3" />
                  </Button>
                ) : (
                  <Button size="icon" variant="ghost" asChild className="h-6 w-6">
                    <Link href={`${basePath}/${item.id}/edit`}><Pencil className="h-3 w-3" /></Link>
                  </Button>
                )}
                
                <Button size="icon" variant="ghost" onClick={() => setDeleteId(item.id as string)} className="h-6 w-6">
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {title}?</AlertDialogTitle>
            <AlertDialogDescription>This will remove this configuration.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { router.delete(`${basePath}/${deleteId}`, { preserveScroll: true }); setDeleteId(null) } }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isNameOption && (
        <FormationNameOptionSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          entityId={entityId}
          option={editingItem}
          onSuccess={() => { router.reload({ only: ['entity'] }) }}
        />
      )}
    </div>
  )
}

export default function EntitiesShow({ entity }: Props) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  return (
    <BackofficeLayout title={entity.name} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Entities', href: '/backoffice/entities' }, { label: entity.name }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">{entity.name}</h1>
          <p className="text-sm text-muted-foreground">{entity.code}</p>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild className="h-8 text-[13px]">
            <Link href={`/backoffice/entities/${entity.id}/edit`}><Pencil className="mr-1.5 h-3 w-3" />Edit</Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowDeleteDialog(true)} className="h-8 text-[13px] text-destructive hover:text-destructive">
            <Trash2 className="mr-1.5 h-3 w-3" />Delete
          </Button>
        </div>

        <DetailGrid items={[
          { label: 'Jurisdiction', value: entity.jurisdiction_label || '—' },
          { label: 'Filing Type', value: entity.filing_type },
          { label: 'State Fee', value: formatCents(entity.state_fee_cents) },
          { label: 'Service Fee', value: formatCents(entity.service_fee_cents) },
          { label: 'Processing', value: `${entity.processing_days_min}–${entity.processing_days_max} days` },
          { label: 'Anonymous Ownership', value: entity.allows_anonymous_ownership ? 'Yes' : 'No' },
          { label: 'Public Disclosure', value: entity.requires_public_member_disclosure ? 'Yes' : 'No' },
        ]} />

        {entity.description && (
          <div className="py-3 border-b border-border/40">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Description</p>
            <p className="text-sm">{entity.description}</p>
          </div>
        )}

        {/* Name Options */}
        <div>
          <SectionHeader title="Name Options" />
          <div className="mt-1 border rounded-lg px-4 py-2">
            <SupportSection title="Name Suffixes" supportType="formation_name_option" entityId={entity.id} items={entity.formation_name_options || []} fieldLabels={[{key:'label',label:'Label'},{key:'suffix',label:'Suffix'},{key:'position',label:'Position'}]} />
          </div>
        </div>

        <div>
          <SectionHeader title="Role Supports" />
          <div className="mt-1 border rounded-lg px-4 py-2">
            <SupportSection title="Owners" supportType="owner" entityId={entity.id} items={entity.supports.owners} fieldLabels={[{key:'min_count',label:'Min'},{key:'max_count',label:'Max'},{key:'public_disclosure',label:'Public'}]} />
            <SupportSection title="Directors" supportType="director" entityId={entity.id} items={entity.supports.directors} fieldLabels={[{key:'requires_board_chair',label:'Chair'},{key:'min_count',label:'Min'},{key:'max_count',label:'Max'},{key:'public_disclosure',label:'Public'}]} />
            <SupportSection title="Presidents" supportType="president" entityId={entity.id} items={entity.supports.presidents} fieldLabels={[{key:'required',label:'Required'}]} />
            <SupportSection title="Secretaries" supportType="secretary" entityId={entity.id} items={entity.supports.secretaries} fieldLabels={[{key:'required',label:'Required'}]} />
            <SupportSection title="Treasurers" supportType="treasurer" entityId={entity.id} items={entity.supports.treasurers} fieldLabels={[{key:'required',label:'Required'}]} />
            <SupportSection title="Managers" supportType="manager" entityId={entity.id} items={entity.supports.managers} fieldLabels={[{key:'allows_member_managed',label:'Member'},{key:'allows_manager_managed',label:'Manager'},{key:'min_count',label:'Min'},{key:'max_count',label:'Max'}]} />
            <SupportSection title="Trustees" supportType="trustee" entityId={entity.id} items={entity.supports.trustees} fieldLabels={[{key:'min_count',label:'Min'},{key:'max_count',label:'Max'},{key:'public_disclosure',label:'Public'}]} />
            <SupportSection title="Incorporators" supportType="incorporator" entityId={entity.id} items={entity.supports.incorporators} fieldLabels={[{key:'required',label:'Required'}]} />
            <SupportSection title="Registered Agents" supportType="registered_agent" entityId={entity.id} items={entity.supports.registered_agents} fieldLabels={[{key:'allows_individual',label:'Individual'},{key:'allows_entity',label:'Entity'},{key:'required',label:'Required'}]} />
            <SupportSection title="Capital" supportType="capital" entityId={entity.id} items={entity.supports.capitals} fieldLabels={[{key:'min_cents',label:'Min'},{key:'max_cents',label:'Max'},{key:'recommended_cents',label:'Recommended'}]} />
          </div>
        </div>

        {/* Document Templates */}
        <DocumentUploader
          title="Document Templates"
          documents={entity.document_templates || []}
          uploadPath={`/backoffice/entities/${entity.id}/document_templates`}
          deletePath={(docId) => `/backoffice/entities/${entity.id}/document_templates/${docId}`}
        />

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {entity.name}?</AlertDialogTitle>
              <AlertDialogDescription>This will permanently delete this entity type. Entities with existing formations cannot be deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => router.delete(`/backoffice/entities/${entity.id}`)}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </BackofficeLayout>
  )
}
